/**
 * StudioSettingsPanel
 *
 * Unified settings panel for Studio (SME/Admin) and Classroom (Faculty) users.
 * Provides the same Theme, Font, and Accessibility controls as the AskOS panel,
 * persisted separately in localStorage under the key "studio_prefs".
 *
 * The panel is mounted inside WhitelabelHeader and triggered by the ⚙ gear button.
 * applyStudioPreferences() is also exported so App.js can apply saved prefs on boot.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Modal, Nav, Button, Form, Row, Col } from 'react-bootstrap';
import { Palette, Type, Accessibility, Sun, Moon, Eye, Wind } from 'lucide-react';

// ── Constants ─────────────────────────────────────────────────────────────────

const THEMES = [
    { key: 'light',         label: 'Light',        icon: '☀️', desc: 'Default white background' },
    { key: 'dark',          label: 'Dark',          icon: '🌙', desc: 'Easy on the eyes at night' },
    { key: 'high-contrast', label: 'High Contrast', icon: '⬛', desc: 'Maximum contrast — WCAG AAA' },
    { key: 'sepia',         label: 'Soft Sepia',    icon: '📜', desc: 'Warm tones for long sessions' },
];

const ACCENTS = [
    { hex: '#2563eb', label: 'Blue (default)' },
    { hex: '#6366f1', label: 'Indigo' },
    { hex: '#10b981', label: 'Emerald' },
    { hex: '#f59e0b', label: 'Amber' },
    { hex: '#8b5cf6', label: 'Violet' },
    { hex: '#ef4444', label: 'Red' },
];

const FONT_SIZES = [
    { key: 'sm', label: 'Small',   scale: 0.875 },
    { key: 'md', label: 'Default', scale: 1.0   },
    { key: 'lg', label: 'Large',   scale: 1.125 },
    { key: 'xl', label: 'X-Large', scale: 1.25  },
];

const SPACING = [
    { key: 'normal',  label: 'Normal',  lh: 1.5  },
    { key: 'relaxed', label: 'Relaxed', lh: 1.75 },
    { key: 'loose',   label: 'Loose',   lh: 2.0  },
];

export const STUDIO_DEFAULT_PREFS = {
    theme:           'light',
    accent:          '#2563eb',
    fontSize:        'md',
    lineSpacing:     'normal',
    reduceMotion:    false,
    dyslexiaFont:    false,
    focusIndicators: true,    // on by default for studio users (WCAG)
    largeCursor:     false,
};

const STORAGE_KEY = 'studio_prefs';

export function loadStudioPrefs() {
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        return raw ? { ...STUDIO_DEFAULT_PREFS, ...JSON.parse(raw) } : { ...STUDIO_DEFAULT_PREFS };
    } catch {
        return { ...STUDIO_DEFAULT_PREFS };
    }
}

export function saveStudioPrefs(prefs) {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(prefs));
}

export function applyStudioPreferences(prefs) {
    const root = document.documentElement;

    // Theme — same data attribute as AskOS so shared CSS rules apply
    root.setAttribute('data-askos-theme', prefs.theme);

    // Accent colour
    root.style.setProperty('--askos-accent', prefs.accent);
    root.style.setProperty('--color-brand-600', prefs.accent);
    root.style.setProperty('--askos-accent-light', prefs.accent + '22');

    // Font scale
    const scale = FONT_SIZES.find(f => f.key === prefs.fontSize)?.scale || 1.0;
    root.style.setProperty('--askos-font-scale', scale);
    root.style.setProperty('--askos-base-size', `${scale}rem`);

    // Line height
    const lh = SPACING.find(l => l.key === prefs.lineSpacing)?.lh || 1.5;
    root.style.setProperty('--askos-line-height', lh);

    // Motion
    if (prefs.reduceMotion) {
        root.style.setProperty('--askos-transition', 'none');
    } else {
        root.style.removeProperty('--askos-transition');
    }

    // Dyslexia font
    if (prefs.dyslexiaFont) {
        root.style.setProperty('--askos-font-family', "'OpenDyslexic', 'Comic Sans MS', cursive");
    } else {
        root.style.removeProperty('--askos-font-family');
    }

    // Focus indicators
    root.setAttribute('data-askos-focus', prefs.focusIndicators ? 'visible' : 'default');

    // Body class toggles (same pattern as AskOS)
    document.body.classList.toggle('askos-reduce-motion',  prefs.reduceMotion);
    document.body.classList.toggle('askos-dyslexia',       prefs.dyslexiaFont);
    document.body.classList.toggle('askos-focus-visible',  prefs.focusIndicators);
    document.body.classList.toggle('askos-large-cursor',   prefs.largeCursor);
    document.body.classList.toggle('askos-theme-dark',     prefs.theme === 'dark');
    document.body.classList.toggle('askos-theme-hc',       prefs.theme === 'high-contrast');
    document.body.classList.toggle('askos-theme-sepia',    prefs.theme === 'sepia');
}


// ── Sub-components ────────────────────────────────────────────────────────────

function ThemeTab({ prefs, onChange }) {
    return (
        <div>
            <p className="text-muted small mb-3">
                Choose a colour scheme for your workspace. Your preference is saved across sessions.
            </p>
            <Row className="g-2 mb-4">
                {THEMES.map(t => (
                    <Col xs={6} key={t.key}>
                        <button
                            onClick={() => onChange({ ...prefs, theme: t.key })}
                            className={`w-100 border rounded-3 p-3 text-start ${prefs.theme === t.key ? 'border-primary bg-primary bg-opacity-10' : 'border-light bg-white'}`}
                            style={{ cursor: 'pointer', transition: 'all 0.15s' }}
                            aria-pressed={prefs.theme === t.key}
                        >
                            <div className="fs-4 mb-1" aria-hidden="true">{t.icon}</div>
                            <div className="fw-semibold small">{t.label}</div>
                            <div className="text-muted" style={{ fontSize: 11 }}>{t.desc}</div>
                        </button>
                    </Col>
                ))}
            </Row>

            <p className="fw-semibold small mb-2">Accent Colour</p>
            <div className="d-flex gap-2 flex-wrap mb-3">
                {ACCENTS.map(a => (
                    <button
                        key={a.hex}
                        onClick={() => onChange({ ...prefs, accent: a.hex })}
                        className="border-0 rounded-circle"
                        style={{
                            width: 30, height: 30,
                            background: a.hex,
                            outline: prefs.accent === a.hex ? `3px solid ${a.hex}` : 'none',
                            outlineOffset: 2,
                            cursor: 'pointer',
                        }}
                        title={a.label}
                        aria-label={`${a.label} accent${prefs.accent === a.hex ? ' (selected)' : ''}`}
                    />
                ))}
                <div className="d-flex align-items-center gap-2 ms-2">
                    <label htmlFor="custom-accent" className="text-muted small mb-0">Custom:</label>
                    <Form.Control
                        id="custom-accent"
                        type="color"
                        value={prefs.accent}
                        onChange={e => onChange({ ...prefs, accent: e.target.value })}
                        style={{ width: 36, height: 30, padding: 2, cursor: 'pointer' }}
                        title="Custom accent colour"
                    />
                </div>
            </div>
        </div>
    );
}

function FontTab({ prefs, onChange }) {
    return (
        <div>
            <p className="text-muted small mb-3">
                Adjust text size and spacing for comfortable reading during long authoring sessions.
            </p>

            <p className="fw-semibold small mb-2">Font Size</p>
            <div className="d-flex gap-2 mb-4">
                {FONT_SIZES.map(f => (
                    <button
                        key={f.key}
                        onClick={() => onChange({ ...prefs, fontSize: f.key })}
                        className={`border rounded px-3 py-2 ${prefs.fontSize === f.key ? 'border-primary text-primary fw-semibold bg-primary bg-opacity-10' : 'border-light text-muted'}`}
                        style={{ cursor: 'pointer', fontSize: `${f.scale}rem` }}
                        aria-pressed={prefs.fontSize === f.key}
                    >
                        {f.label}
                    </button>
                ))}
            </div>

            <p className="fw-semibold small mb-2">Line Spacing</p>
            <div className="d-flex gap-2">
                {SPACING.map(s => (
                    <button
                        key={s.key}
                        onClick={() => onChange({ ...prefs, lineSpacing: s.key })}
                        className={`border rounded px-3 py-2 flex-fill ${prefs.lineSpacing === s.key ? 'border-primary text-primary bg-primary bg-opacity-10' : 'border-light text-muted'}`}
                        style={{ cursor: 'pointer' }}
                        aria-pressed={prefs.lineSpacing === s.key}
                    >
                        {s.label}
                    </button>
                ))}
            </div>
        </div>
    );
}

function AccessibilityTab({ prefs, onChange }) {
    const toggle = (key) => onChange({ ...prefs, [key]: !prefs[key] });

    const options = [
        {
            key: 'focusIndicators',
            label: 'Enhanced Focus Indicators',
            desc: 'Adds a prominent visible ring around keyboard-focused elements (WCAG 2.4.7)',
            icon: <Eye size={18} />,
        },
        {
            key: 'reduceMotion',
            label: 'Reduce Motion',
            desc: 'Disables animations and transitions. Useful for vestibular disorders (WCAG 2.3.3)',
            icon: <Wind size={18} />,
        },
        {
            key: 'dyslexiaFont',
            label: 'Dyslexia-Friendly Font',
            desc: 'Switches to OpenDyslexic font to improve letter recognition',
            icon: <Type size={18} />,
        },
        {
            key: 'largeCursor',
            label: 'Large Cursor',
            desc: 'Enlarges the mouse pointer for easier tracking on high-DPI displays',
            icon: <Eye size={18} />,
        },
    ];

    return (
        <div>
            <p className="text-muted small mb-4">
                These settings help meet WCAG 2.1 AA accessibility standards and support users with visual, motor, or cognitive needs.
            </p>
            {options.map(({ key, label, desc, icon }) => (
                <div
                    key={key}
                    className={`d-flex align-items-start gap-3 p-3 rounded-3 mb-3 border ${prefs[key] ? 'border-primary bg-primary bg-opacity-10' : 'border-light bg-light'}`}
                    style={{ cursor: 'pointer' }}
                    onClick={() => toggle(key)}
                    role="checkbox"
                    aria-checked={prefs[key]}
                    tabIndex={0}
                    onKeyDown={e => (e.key === ' ' || e.key === 'Enter') && toggle(key)}
                >
                    <div className={prefs[key] ? 'text-primary' : 'text-muted'} aria-hidden="true">{icon}</div>
                    <div className="flex-grow-1">
                        <div className="fw-semibold small">{label}</div>
                        <div className="text-muted" style={{ fontSize: 12 }}>{desc}</div>
                    </div>
                    <Form.Check
                        type="switch"
                        checked={prefs[key]}
                        onChange={() => toggle(key)}
                        onClick={e => e.stopPropagation()}
                        aria-label={label}
                        className="mb-0"
                    />
                </div>
            ))}
        </div>
    );
}


// ── Main component ────────────────────────────────────────────────────────────

export default function StudioSettingsPanel({ show, onHide, isClassroom, userEmail }) {
    const [activeTab, setActiveTab] = useState('theme');
    const [prefs, setPrefs] = useState(loadStudioPrefs);
    const [saved, setSaved] = useState(false);

    // Apply prefs live as the user changes them
    const handleChange = useCallback((newPrefs) => {
        setPrefs(newPrefs);
        applyStudioPreferences(newPrefs);
    }, []);

    const handleSave = () => {
        saveStudioPrefs(prefs);
        setSaved(true);
        setTimeout(() => { setSaved(false); onHide(); }, 800);
    };

    const handleReset = () => {
        setPrefs({ ...STUDIO_DEFAULT_PREFS });
        applyStudioPreferences(STUDIO_DEFAULT_PREFS);
    };

    // Reload saved prefs each time panel opens
    useEffect(() => {
        if (show) setPrefs(loadStudioPrefs());
    }, [show]);

    const contextLabel = isClassroom ? 'Classroom Portal' : 'Studio';

    const tabs = [
        { key: 'theme',         label: 'Theme',           icon: <Palette size={16} /> },
        { key: 'font',          label: 'Font & Spacing',  icon: <Type size={16} /> },
        { key: 'accessibility', label: 'Accessibility',   icon: <Accessibility size={16} /> },
    ];

    return (
        <Modal show={show} onHide={onHide} centered size="lg" aria-labelledby="studio-settings-title">
            <Modal.Header closeButton className="border-bottom">
                <Modal.Title id="studio-settings-title" style={{ fontSize: 16 }}>
                    ⚙ {contextLabel} Settings
                    {userEmail && <span className="text-muted fw-normal ms-2" style={{ fontSize: 13 }}>— {userEmail}</span>}
                </Modal.Title>
            </Modal.Header>
            <Modal.Body className="p-0" style={{ minHeight: 420 }}>
                <div className="d-flex h-100">
                    {/* Sidebar nav */}
                    <div className="border-end py-3 px-2" style={{ minWidth: 160, background: '#f9fafb' }}>
                        {tabs.map(t => (
                            <button
                                key={t.key}
                                onClick={() => setActiveTab(t.key)}
                                className={`d-flex align-items-center gap-2 w-100 border-0 rounded px-3 py-2 mb-1 text-start ${
                                    activeTab === t.key
                                        ? 'bg-primary text-white fw-semibold'
                                        : 'bg-transparent text-muted'
                                }`}
                                style={{ fontSize: 13, cursor: 'pointer', transition: 'all 0.15s' }}
                                aria-current={activeTab === t.key ? 'page' : undefined}
                            >
                                <span aria-hidden="true">{t.icon}</span>
                                {t.label}
                            </button>
                        ))}
                    </div>

                    {/* Tab content */}
                    <div className="flex-grow-1 p-4 overflow-auto">
                        {activeTab === 'theme'         && <ThemeTab prefs={prefs} onChange={handleChange} />}
                        {activeTab === 'font'          && <FontTab prefs={prefs} onChange={handleChange} />}
                        {activeTab === 'accessibility' && <AccessibilityTab prefs={prefs} onChange={handleChange} />}
                    </div>
                </div>
            </Modal.Body>
            <Modal.Footer className="border-top d-flex justify-content-between">
                <Button variant="link" className="text-muted p-0" onClick={handleReset} style={{ fontSize: 13 }}>
                    Reset to defaults
                </Button>
                <div className="d-flex gap-2">
                    <Button variant="outline-secondary" size="sm" onClick={onHide}>Cancel</Button>
                    <Button variant="primary" size="sm" onClick={handleSave}>
                        {saved ? '✓ Saved!' : 'Save Settings'}
                    </Button>
                </div>
            </Modal.Footer>
        </Modal>
    );
}
